import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { useAuth } from "@/hooks/useAuth";
import { useDocumentosSociales, TipoDocumentoSocial } from "@/hooks/useDocumentosSociales";
import { DocumentosList } from "@/components/social/DocumentosList";
import { SubirDocumentoModal } from "@/components/social/SubirDocumentoModal";
import { ArrowLeft, Upload, FileText } from "lucide-react";
import Footer from "@/components/Footer";

const TIPOS_NOMBRES: Record<TipoDocumentoSocial, string> = {
  soporte: "Soportes",
  acta: "Actas",
  lista_asistencia: "Listas de Asistencia",
  foto: "Fotos",
  video: "Videos",
  audio: "Audios",
  informe: "Informes",
  correspondencia_entrada: "Correspondencia Entrada",
  correspondencia_salida: "Correspondencia Salida",
  pqrs: "PQRS",
  otros: "Otros",
};

const DocumentosSociales = () => {
  const navigate = useNavigate();
  const { signOut } = useAuth();
  const [searchParams] = useSearchParams();
  const tipo = searchParams.get('tipo') as TipoDocumentoSocial || 'soporte';
  
  const [modalOpen, setModalOpen] = useState(false);
  
  const {
    documentos,
    loading,
    uploading,
    uploadDocumento,
    deleteDocumento,
    getEstadisticas,
  } = useDocumentosSociales(tipo);

  const estadisticas = getEstadisticas();

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-sm border-b border-border shadow-sm">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <Button 
              variant="ghost" 
              size="icon"
              onClick={() => navigate("/herramientas")}
            >
              <ArrowLeft className="w-6 h-6" />
            </Button>
            <div>
              <h1 className="text-xl font-bold text-foreground">{TIPOS_NOMBRES[tipo]}</h1>
              <p className="text-sm text-muted-foreground">Gestión de documentos</p>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <Button 
              variant="outline" 
              onClick={() => navigate("/herramientas")}
            >
              Volver
            </Button>
            <Button variant="outline" size="sm" onClick={signOut}>
              Cerrar Sesión
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="pt-32 lg:pt-24 pb-12">
        <div className="container mx-auto px-4">
          <div className="max-w-7xl mx-auto space-y-6">
            {/* Stats */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Card>
                <CardHeader className="pb-3">
                  <CardDescription>Total de Documentos</CardDescription>
                  <CardTitle className="text-3xl">{estadisticas.total}</CardTitle>
                </CardHeader>
              </Card>
              
              <Card>
                <CardHeader className="pb-3">
                  <CardDescription>Tamaño Total</CardDescription>
                  <CardTitle className="text-3xl">
                    {(estadisticas.tamanioTotal / (1024 * 1024)).toFixed(1)} MB
                  </CardTitle>
                </CardHeader>
              </Card>

              <Card>
                <CardHeader className="pb-3">
                  <CardDescription>Acción Rápida</CardDescription>
                  <Button 
                    className="w-full mt-2 bg-viable-orange hover:bg-viable-orange-dark"
                    onClick={() => setModalOpen(true)}
                  >
                    <Upload className="w-4 h-4 mr-2" />
                    Subir Documento
                  </Button>
                </CardHeader>
              </Card>
            </div>

            {/* Documents List */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <FileText className="w-5 h-5 text-viable-orange" />
                  <h2 className="text-xl font-semibold">Documentos</h2>
                </div>
                <Button 
                  onClick={() => setModalOpen(true)}
                  className="bg-viable-orange hover:bg-viable-orange-dark"
                >
                  <Upload className="w-4 h-4 mr-2" />
                  Subir Documento
                </Button>
              </div>

              <DocumentosList
                documentos={documentos}
                onDelete={deleteDocumento}
                loading={loading}
              />
            </div>
          </div>
        </div>
      </main>

      {/* Upload Modal */}
      <SubirDocumentoModal
        open={modalOpen}
        onOpenChange={setModalOpen}
        tipoDocumento={tipo}
        onUpload={uploadDocumento}
        uploading={uploading}
      />
      <Footer />
    </div>
  );
};

export default DocumentosSociales;
